import type { Lesson, Subject } from "@ts/interfaces/terms/api/termsApiInterface";
import type {
  GradeStructureApiResponse,
  StructureTerm,
  StructureSubject,
  StructureWeek,
  StructureLesson
} from "@ts/interfaces/structure/api/gradeStructureApiInterface";
import { COMPLETION_STATUS, type CompletionStatus } from "@ts/enums/CompletionStatus";
import type { GradeProgressApiResponse } from "@ts/interfaces/progress/api/gradeProgressApiInterface";
import { asArray } from "./mergeTerms";

function statusByUsageKey(
  progress: GradeProgressApiResponse | null
): Map<string, CompletionStatus> {
  const map = new Map<string, CompletionStatus>();
  // progress.lessons is keyed by usage_key → { status }.
  for (const [usageKey, lesson] of Object.entries(progress?.lessons ?? {})) {
    map.set(usageKey, lesson.status);
  }
  return map;
}

/**
 * Rebuilds the legacy week-lessons Subject[] for one term+week: structure gives
 * the subject/lesson tree, progress gives each lesson's status (joined on
 * structure.key === progress.usage_key). Only subjects that have the week are
 * included, preserving structure's (already-sorted) order.
 */
export function mergeWeekLessons(
  structure: GradeStructureApiResponse | null,
  progress: GradeProgressApiResponse | null,
  termId: number,
  weekId: number
): Subject[] {
  if (!structure) return [];

  const structureTerm = asArray<StructureTerm>(structure.terms).find(
    t => t.id === termId
  );
  if (!structureTerm) return [];

  const statusMap = statusByUsageKey(progress);

  const subjects: Subject[] = [];
  for (const subject of asArray<StructureSubject>(structureTerm.subjects)) {
    const week = asArray<StructureWeek>(subject.weeks).find(
      w => w.id === weekId
    );
    if (!week) continue;

    const lessons: Lesson[] = asArray<StructureLesson>(week.lessons).map(
      lesson => ({
        id: lesson.number,
        key: lesson.key,
        name: lesson.name,
        description: lesson.description,
        status: statusMap.get(lesson.key) ?? COMPLETION_STATUS.NOT_STARTED
      })
    );

    subjects.push({
      key: subject.key,
      name: subject.name,
      type: subject.type,
      slug: subject.slug,
      plp_grade_code: subject.plp_grade_code,
      plp_grade_name: subject.plp_grade_name,
      grade_subject_uuid: subject.grade_subject_uuid,
      grade_subject_id: subject.portal_grade_subject_id,
      lessons
    });
  }

  return subjects;
}
